import { DeleteOutlined, UndoOutlined } from "@ant-design/icons";
import { Badge, Button, Card, Col, Form, InputNumber, Popconfirm, Row, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { SharedQuota, StudyProgramQuota } from "@bindeleddet/core/types";
import RelevantStudyYearsDisplay from "components/RelevantStudyYearsDisplay/RelevantStudyYearsDisplay";
import React, { useEffect, useRef, useState } from "react";
import { findDuplicateQuotaCombination, hasDuplicateQuotaCombinations } from "helpers/quotaValidation";
import { debounce } from "helpers/functions";

interface Props {
  currentSharedQuota: SharedQuota | undefined;
  sharedQuotas: SharedQuota[];
  studyProgramQuotas: StudyProgramQuota[];
  handleQuotas: (sq: SharedQuota[], spq: StudyProgramQuota[], csq: SharedQuota | undefined) => void;
}

const { Text } = Typography;

const TotalSeatsStep: React.FC<Props> = ({ sharedQuotas, studyProgramQuotas, currentSharedQuota, handleQuotas }) => {
  const [connectedQuotas, setConnectedQuotas] = useState<(SharedQuota & { studyProgramQuotas: StudyProgramQuota[] })[]>(
    []
  );
  const [form] = useForm();
  const [loadingSave, setLoadingSave] = useState<boolean>(false);
  const [localStudyProgramQuotas, setLocalStudyProgramQuotas] = useState<StudyProgramQuota[]>([]);
  const [deletedStudyProgramQuotaIds, setDeletedStudyProgramQuotaIds] = useState<Set<number>>(new Set());

  // Watch form values for reactive validation
  const watchedMaxStudentsAllowed = Form.useWatch("max_students_allowed", form);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (currentSharedQuota) {
      const currentQuotaStudyProgramQuotas = studyProgramQuotas.filter(
        spq => spq.shared_quota === currentSharedQuota.id
      );
      setLocalStudyProgramQuotas(currentQuotaStudyProgramQuotas);
      setDeletedStudyProgramQuotaIds(new Set());
      const fieldValues: Record<string, number> = {
        max_students_allowed: currentSharedQuota.max_students_allowed,
      };
      currentQuotaStudyProgramQuotas.forEach(spq => {
        fieldValues[`students_allowed_${spq.id}`] = spq.students_allowed;
      });
      form.setFieldsValue(fieldValues);
    } else {
      setLocalStudyProgramQuotas([]);
      setDeletedStudyProgramQuotaIds(new Set());
    }
  }, [currentSharedQuota, studyProgramQuotas, form]);

  useEffect(() => {
    if (sharedQuotas.length < 1) return;
    const connected = sharedQuotas.map(sharedquota => ({
      ...sharedquota,
      studyProgramQuotas: studyProgramQuotas.filter(studyquota => studyquota.shared_quota === sharedquota.id),
    }));
    setConnectedQuotas(connected);
  }, [sharedQuotas, studyProgramQuotas]);

  // Debounced validation function to avoid running on every keystroke
  const validateFieldsDebounced = useRef(
    debounce(() => {
      if (!currentSharedQuota || localStudyProgramQuotas.length === 0) {
        setValidationErrors({});
        return;
      }

      const fieldsToValidate = [
        "max_students_allowed",
        ...localStudyProgramQuotas
          .filter(spq => !deletedStudyProgramQuotaIds.has(spq.id))
          .map(spq => `students_allowed_${spq.id}`),
      ];

      form
        .validateFields(fieldsToValidate)
        .then(() => {
          setValidationErrors({});
        })
        .catch(errorInfo => {
          const errors: Record<string, string[]> = {};
          if (errorInfo.errorFields) {
            errorInfo.errorFields.forEach((field: { name: string[]; errors: string[] }) => {
              errors[field.name[0]] = field.errors;
            });
          }
          setValidationErrors(errors);
        });
    }, 300)
  ).current;

  // Revalidate when form values change and check for errors
  useEffect(() => {
    validateFieldsDebounced();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedMaxStudentsAllowed,
    localStudyProgramQuotas.length,
    currentSharedQuota?.id,
    deletedStudyProgramQuotaIds.size,
  ]);

  // Check if form has validation errors
  const hasValidationErrors = () => {
    if (!currentSharedQuota) return false;
    return Object.keys(validationErrors).length > 0;
  };

  const toggleYearLevel = (studyProgramQuotaId: number, yearLevel: number) => {
    setLocalStudyProgramQuotas(prev => {
      const updated = prev.map(spq => {
        if (spq.id === studyProgramQuotaId) {
          const currentYearLevels = [...spq.year_levels_accepted];
          const index = currentYearLevels.indexOf(yearLevel);
          if (index > -1) {
            // Remove year level, but ensure at least one remains
            if (currentYearLevels.length > 1) {
              currentYearLevels.splice(index, 1);
            } else {
              message("warning", "Minst ett trinn må være valgt");
              return spq;
            }
          } else {
            // Add year level
            currentYearLevels.push(yearLevel);
            currentYearLevels.sort((a, b) => a - b);
          }
          return { ...spq, year_levels_accepted: currentYearLevels };
        }
        return spq;
      });

      // Check if the updated combination already exists
      const updatedSpq = updated.find(spq => spq.id === studyProgramQuotaId);
      if (updatedSpq) {
        const duplicate = findDuplicateQuotaCombination(updatedSpq, updated, studyProgramQuotaId);
        if (duplicate) {
          message("warning", "Denne nøyaktige kombinasjonen av linje og trinn finnes allerede i kvoten");
          return prev; // Return previous state without changes
        }
      }

      return updated;
    });
  };

  const deleteSharedQuota = (id: number) => {
    axios
      .delete(`/events/shared-quotas/${id}/`)
      .then(() => {
        const newSharedQuotas = sharedQuotas.filter(quota => quota.id !== id);
        const newStudyProgramQuotas = studyProgramQuotas.filter(quota => quota.shared_quota !== id);
        handleQuotas(newSharedQuotas, newStudyProgramQuotas, currentSharedQuota);
        setConnectedQuotas(connectedQuotas.filter(quota => quota.id !== id));
        message("success", "Kvoten er slettet.");
      })
      .catch(() => message("error", "Noe gikk galt ved sletting av kvote."));
  };

  const saveSharedQuota = () => {
    form.validateFields().then(values => {
      setLoadingSave(true);
      const maxStudentsAllowed = values.max_students_allowed;
      const updatedStudyProgramQuotas = localStudyProgramQuotas.map(spq => ({
        ...spq,
        students_allowed: values[`students_allowed_${spq.id}`] ?? spq.students_allowed,
      }));

      // Check for duplicate combinations of study_programme and year_levels_accepted
      const duplicateCheck = hasDuplicateQuotaCombinations(updatedStudyProgramQuotas);

      if (duplicateCheck) {
        message("error", "Det finnes allerede en linje med samme kombinasjon av trinn i kvoten");
        setLoadingSave(false);
        return;
      }

      // Filter out deleted quotas
      const nonDeletedQuotas = updatedStudyProgramQuotas.filter(spq => !deletedStudyProgramQuotaIds.has(spq.id));

      // If all quotas are marked for deletion, delete the entire shared quota instead
      if (nonDeletedQuotas.length === 0 && currentSharedQuota) {
        deleteSharedQuota(currentSharedQuota.id);
        setLoadingSave(false);
        return;
      }

      // Check if anything has changed
      const hasSharedQuotaChanged = maxStudentsAllowed !== currentSharedQuota?.max_students_allowed;
      const hasStudyProgramQuotasChanged = nonDeletedQuotas.some(updated => {
        const original = studyProgramQuotas.find(spq => spq.id === updated.id);
        if (!original) return true; // New quota, consider it changed
        return (
          updated.students_allowed !== original.students_allowed ||
          JSON.stringify([...updated.year_levels_accepted].sort((a, b) => a - b)) !==
            JSON.stringify([...original.year_levels_accepted].sort((a, b) => a - b))
        );
      });
      const hasDeletions = deletedStudyProgramQuotaIds.size > 0;

      if (!hasSharedQuotaChanged && !hasStudyProgramQuotasChanged && !hasDeletions) {
        handleQuotas(sharedQuotas, studyProgramQuotas, undefined);
        setLoadingSave(false);
        return;
      }

      // Update shared quota if changed
      const sharedQuotaPromise = hasSharedQuotaChanged
        ? axios.put(`/events/shared-quotas/${currentSharedQuota?.id}/`, {
            ...currentSharedQuota,
            max_students_allowed: maxStudentsAllowed,
          })
        : Promise.resolve({ data: currentSharedQuota });

      // Update all non-deleted study program quotas
      const studyProgramQuotaPromises = nonDeletedQuotas.map(updatedSpq =>
        axios.put(`/events/${currentSharedQuota?.event}/study-program-quotas/${updatedSpq.id}/`, updatedSpq)
      );

      // Delete marked study program quotas
      const deletePromises = Array.from(deletedStudyProgramQuotaIds).map(id =>
        axios.delete(`/events/${currentSharedQuota?.event}/study-program-quotas/${id}/`)
      );

      Promise.all([sharedQuotaPromise, ...studyProgramQuotaPromises, ...deletePromises])
        .then(([sharedQuotaResponse, ...responses]) => {
          const studyProgramQuotaResponses = responses.slice(0, studyProgramQuotaPromises.length);
          const newSharedQuotas = sharedQuotas.map(quota =>
            quota.id === sharedQuotaResponse.data.id ? sharedQuotaResponse.data : quota
          );
          const newStudyProgramQuotas = studyProgramQuotas
            .filter(existingSpq => !deletedStudyProgramQuotaIds.has(existingSpq.id))
            .map(existingSpq => {
              const updated = studyProgramQuotaResponses.find(res => res.data.id === existingSpq.id);
              return updated ? updated.data : existingSpq;
            });
          handleQuotas(newSharedQuotas, newStudyProgramQuotas, undefined);
          message("success", "Kvoten er lagret.");
        })
        .catch(() => message("error", "Noe gikk galt ved lagring av kvote."))
        .finally(() => setLoadingSave(false));
    });
  };

  const markStudyProgramQuotaForDeletion = (id: number) => {
    // Get non-deleted study program quotas
    const nonDeletedQuotas = localStudyProgramQuotas.filter(spq => !deletedStudyProgramQuotaIds.has(spq.id));
    const quotaToDelete = localStudyProgramQuotas.find(spq => spq.id === id);

    if (!quotaToDelete) return;

    // Check if deleting this would leave any non-deleted quotas
    const remainingQuotas = nonDeletedQuotas.filter(spq => spq.id !== id);

    if (remainingQuotas.length === 0) {
      message("warning", "Kan ikke slette alle linjer i kvoten");
      return;
    }

    // Check if deleting this would make sum of remaining quotas less than total seats
    const currentMaxStudentsAllowed = form.getFieldValue("max_students_allowed") ?? watchedMaxStudentsAllowed;
    const sumOfRemaining = remainingQuotas.reduce((sum, spq) => {
      const localValue = form.getFieldValue(`students_allowed_${spq.id}`);
      return sum + (localValue ?? spq.students_allowed);
    }, 0);

    if (currentMaxStudentsAllowed > sumOfRemaining) {
      message("warning", "Kan ikke slette linjen. Totale plasser vil overstige summen av plassene per linje.");
      return;
    }

    // Mark for deletion
    setDeletedStudyProgramQuotaIds(prev => new Set(prev).add(id));
  };

  const restoreStudyProgramQuota = (id: number) => {
    setDeletedStudyProgramQuotaIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  return (
    <>
      <Card title="2. Plasser per kvote" style={{ margin: "20px 0" }}>
        <Row gutter={[16, 16]}>
          {connectedQuotas.length < 1 && (
            <Card style={{ width: "100%" }}>
              Ingen kvoter er opprettet ennå. Opprett første kvote ved å legge til trinn/linje og velge &quot;Ny
              kvote&quot; i dropdown-menyen.
            </Card>
          )}
          {connectedQuotas.map((quota, index) => {
            const isEditing = currentSharedQuota?.id === quota.id;
            return (
              <Col span={24} key={quota.id}>
                <Badge.Ribbon text={`Kvote ${index + 1}`} placement="end">
                  <Card
                    style={
                      quota.id === currentSharedQuota?.id
                        ? { background: "var(--neutral-alt)", border: "2px solid var(--primary-color)" }
                        : undefined
                    }
                  >
                    <Row gutter={[8, 8]}>
                      <Col md={8} xs={14}>
                        <Text strong>Linjer:</Text>
                        {quota.studyProgramQuotas.map(studyProgramQuota => {
                          const isDeleted = isEditing && deletedStudyProgramQuotaIds.has(studyProgramQuota.id);
                          return (
                            <Row
                              key={studyProgramQuota.id}
                              style={{
                                alignItems: "center",
                                minHeight: isEditing ? "32px" : "auto",
                                marginBottom: isEditing ? "2px" : "0",
                                opacity: isDeleted ? 0.3 : 1,
                                filter: isDeleted ? "grayscale(100%)" : "none",
                              }}
                            >
                              {isEditing &&
                                (isDeleted ? (
                                  <UndoOutlined
                                    onClick={() => restoreStudyProgramQuota(studyProgramQuota.id)}
                                    style={{ color: "var(--primary-color)", cursor: "pointer" }}
                                  />
                                ) : (
                                  <DeleteOutlined
                                    onClick={() => markStudyProgramQuotaForDeletion(studyProgramQuota.id)}
                                    style={{ color: "red", cursor: "pointer" }}
                                  />
                                ))}
                              {isEditing ? (
                                <Col>
                                  <Row>
                                    {[1, 2, 3, 4, 5].map(yearLevel => {
                                      const localSpq = localStudyProgramQuotas.find(
                                        spq => spq.id === studyProgramQuota.id
                                      );
                                      const yearLevelsAccepted =
                                        localSpq?.year_levels_accepted ?? studyProgramQuota.year_levels_accepted;
                                      const isSelected = yearLevelsAccepted.includes(yearLevel);
                                      return (
                                        <Col style={{ padding: "0.1em" }} key={yearLevel}>
                                          <span
                                            role="button"
                                            tabIndex={isDeleted ? -1 : 0}
                                            onClick={() =>
                                              !isDeleted && toggleYearLevel(studyProgramQuota.id, yearLevel)
                                            }
                                            onKeyDown={e => {
                                              if (!isDeleted && (e.key === "Enter" || e.key === " ")) {
                                                e.preventDefault();
                                                toggleYearLevel(studyProgramQuota.id, yearLevel);
                                              }
                                            }}
                                            style={{ cursor: isDeleted ? "not-allowed" : "pointer" }}
                                          >
                                            <Badge
                                              count={yearLevel}
                                              style={{
                                                backgroundColor: isSelected
                                                  ? "var(--bindeleddet-color)"
                                                  : "var(--neutral-alter)",
                                                color: isSelected ? "var(--neutral)" : "var(--neutral-alt)",
                                              }}
                                            />
                                          </span>
                                        </Col>
                                      );
                                    })}
                                  </Row>
                                </Col>
                              ) : (
                                <RelevantStudyYearsDisplay
                                  yearLevelsAccepted={studyProgramQuota.year_levels_accepted}
                                />
                              )}
                              {studyProgramQuota.study_programme_shortname}
                            </Row>
                          );
                        })}
                      </Col>

                      <Col md={6} xs={10}>
                        <Text strong>Maks per linje:</Text>
                        {isEditing ? (
                          <Form form={form}>
                            {quota.studyProgramQuotas.map(studyProgramQuota => {
                              const isDeleted = deletedStudyProgramQuotaIds.has(studyProgramQuota.id);
                              return (
                                <Row
                                  key={studyProgramQuota.id}
                                  style={{
                                    minHeight: "32px",
                                    alignItems: "center",
                                    marginBottom: "2px",
                                    opacity: isDeleted ? 0.3 : 1,
                                    filter: isDeleted ? "grayscale(100%)" : "none",
                                  }}
                                >
                                  <Form.Item
                                    name={`students_allowed_${studyProgramQuota.id}`}
                                    style={{ margin: 0, width: "auto" }}
                                    validateTrigger={["onChange", "onBlur"]}
                                    dependencies={["max_students_allowed"]}
                                    rules={[
                                      { required: true, message: "Må være mer enn 0" },
                                      {
                                        validator: (_, value) => {
                                          if (!value || value <= 0) {
                                            return Promise.resolve();
                                          }
                                          // Don't validate deleted quotas
                                          if (isDeleted) {
                                            return Promise.resolve();
                                          }
                                          const currentMaxStudentsAllowed =
                                            form.getFieldValue("max_students_allowed") ?? watchedMaxStudentsAllowed;
                                          if (value > currentMaxStudentsAllowed) {
                                            return Promise.reject(
                                              new Error(
                                                `Kan ikke være høyere enn plasser totalt (${currentMaxStudentsAllowed})`
                                              )
                                            );
                                          }
                                          return Promise.resolve();
                                        },
                                      },
                                    ]}
                                  >
                                    <InputNumber min={1} style={{ width: "80px" }} disabled={isDeleted} />
                                  </Form.Item>
                                </Row>
                              );
                            })}
                          </Form>
                        ) : (
                          quota.studyProgramQuotas.map(studyProgramQuota => (
                            <Row
                              key={studyProgramQuota.id}
                              style={{
                                minHeight: "25px",
                                alignItems: "center",
                              }}
                            >
                              <Text>{studyProgramQuota.students_allowed}</Text>
                            </Row>
                          ))
                        )}
                      </Col>

                      <Col md={6} xs={24}>
                        <Text strong>Plasser totalt:</Text> <br />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "calc(100% - 24px)",
                          }}
                        >
                          {currentSharedQuota?.id === quota.id ? (
                            <Form form={form}>
                              <Form.Item
                                name="max_students_allowed"
                                validateTrigger={["onChange", "onBlur"]}
                                dependencies={localStudyProgramQuotas.map(spq => `students_allowed_${spq.id}`)}
                                rules={[
                                  { required: true, message: "Angi hvor mange plasser linjene i kvoten får totalt" },
                                  {
                                    validator: (_, value) => {
                                      if (!value || value <= 0) {
                                        return Promise.resolve();
                                      }
                                      // Use local values if editing, otherwise use original values
                                      // Exclude deleted quotas from validation
                                      const currentQuotaStudyProgramQuotas =
                                        currentSharedQuota?.id === quota.id
                                          ? localStudyProgramQuotas.filter(
                                              spq => !deletedStudyProgramQuotaIds.has(spq.id)
                                            )
                                          : quota.studyProgramQuotas;
                                      const maxAllowed = currentQuotaStudyProgramQuotas.reduce((sum, spq) => {
                                        if (currentSharedQuota?.id === quota.id) {
                                          const localValue = form.getFieldValue(`students_allowed_${spq.id}`);
                                          return sum + (localValue ?? spq.students_allowed);
                                        }
                                        return sum + spq.students_allowed;
                                      }, 0);
                                      if (value > maxAllowed) {
                                        return Promise.reject(
                                          new Error(
                                            `Totale plasser kan ikke overstige summen av maks tillatte plasser per linje (${maxAllowed})`
                                          )
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  },
                                ]}
                              >
                                <InputNumber />
                              </Form.Item>
                            </Form>
                          ) : (
                            quota.max_students_allowed
                          )}
                        </div>
                      </Col>

                      <Col md={3} xs={24}>
                        <div
                          style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}
                        >
                          <Button
                            onClick={() =>
                              currentSharedQuota?.id === quota.id
                                ? saveSharedQuota()
                                : handleQuotas(
                                    sharedQuotas,
                                    studyProgramQuotas,
                                    currentSharedQuota?.id === quota.id ? undefined : quota
                                  )
                            }
                            size="small"
                            style={{ width: "100%", marginBottom: "2px" }}
                            loading={loadingSave && currentSharedQuota?.id === quota.id}
                            disabled={currentSharedQuota?.id === quota.id && hasValidationErrors()}
                          >
                            {currentSharedQuota?.id === quota.id ? "Lagre" : "Endre"}
                          </Button>
                          {currentSharedQuota?.id === quota.id ? (
                            <Button
                              style={{ width: "100%" }}
                              size="small"
                              onClick={() => {
                                form.resetFields();
                                setLocalStudyProgramQuotas([]);
                                setDeletedStudyProgramQuotaIds(new Set());
                                handleQuotas(sharedQuotas, studyProgramQuotas, undefined);
                              }}
                            >
                              Avbryt
                            </Button>
                          ) : (
                            <Popconfirm
                              title="Slett kvote"
                              okText="Slett"
                              cancelText="Avbryt"
                              onConfirm={() => deleteSharedQuota(quota.id)}
                            >
                              <Button danger style={{ width: "100%" }} size="small">
                                Slett
                              </Button>
                            </Popconfirm>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Badge.Ribbon>
              </Col>
            );
          })}
        </Row>
      </Card>
    </>
  );
};

export default TotalSeatsStep;
